/**
 * Firestore Users Service
 * 
 * Handles user metadata for messaging:
 * - Online/offline status
 * - Typing indicators
 * - Drafts
 * - Unread counts
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  serverTimestamp,
  deleteField,
  arrayUnion,
  arrayRemove,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { getFirestoreDb, isFirebaseSignedIn } from '../firebase';
import { COLLECTIONS, TYPING_TIMEOUT, sanitizeAvatarUrl } from './schema';

const db = () => getFirestoreDb();

// Backend origin for the server-side archive fan-out (same pattern as messages.js)
const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Initialize or update user in Firestore
 * This syncs the user's profile data (name, avatar) from the app to Firestore
 * Called on every login to ensure data stays in sync
 */
export async function initializeUser(user) {
  const userRef = doc(db(), COLLECTIONS.USERS, user.id);
  const snapshot = await getDoc(userRef);
  
  // Use name from user object, fallback to email prefix if name is truly empty or is an email
  const isEmailLikeName = user.name && user.name.includes('@');
  const userName = user.name && !isEmailLikeName ? user.name : (user.email?.split('@')[0] || user.email);
  
  const userData = {
    name: userName,
    avatar: sanitizeAvatarUrl(user.avatar),
    lastSeen: serverTimestamp(),
    online: true
  };
  
  if (snapshot.exists()) {
    const existingData = snapshot.data();
    // Check if name or avatar has changed - if so, update conversations too
    const nameChanged = existingData.name !== userName;
    const avatarChanged = existingData.avatar !== userData.avatar;
    
    await updateDoc(userRef, userData);
    
    // If name or avatar changed, update participantDetails in all conversations
    if (nameChanged || avatarChanged) {
      await updateUserInConversations(user.id, userName, userData.avatar);
    }
  } else {
    await setDoc(userRef, {
      ...userData,
      typing: null,
      drafts: {},
      unreadCounts: {}
    });
  }
  
  return userData;
}

/**
 * Update user's name/avatar in all their conversations
 * This keeps participantDetails in sync when a user updates their profile
 */
async function updateUserInConversations(userId, newName, newAvatar) {
  try {
    const convsRef = collection(db(), COLLECTIONS.CONVERSATIONS);
    const q = query(convsRef, where('participants', 'array-contains', userId));
    const snapshot = await getDocs(q);
    
    console.log(`Updating user ${userId} in ${snapshot.docs.length} conversations with name: ${newName}, avatar: ${newAvatar}`);
    
    const updatePromises = snapshot.docs.map(async (convDoc) => {
      const convRef = doc(db(), COLLECTIONS.CONVERSATIONS, convDoc.id);
      const updateData = {
        [`participantDetails.${userId}.name`]: newName
      };
      // Only update avatar if provided — sanitize to prevent base64 data
      if (newAvatar !== undefined && newAvatar !== null) {
        updateData[`participantDetails.${userId}.avatar`] = sanitizeAvatarUrl(newAvatar);
      }
      await updateDoc(convRef, updateData);
    });
    
    await Promise.all(updatePromises);
    console.log(`Successfully updated user info in ${snapshot.docs.length} conversations`);
  } catch (err) {
    console.error('Error updating user in conversations:', err);
    // Don't throw - this is a non-critical update
  }
}

/**
 * Update user's profile (name and/or avatar) in Firestore
 * Call this when a user updates their profile settings
 * 
 * @param {string} userId - The user's ID
 * @param {Object} updates - { name?: string, avatar?: string }
 */
export async function updateUserProfile(userId, updates) {
  try {
    const userRef = doc(db(), COLLECTIONS.USERS, userId);
    const snapshot = await getDoc(userRef);
    
    const updateData = {
      lastSeen: serverTimestamp()
    };
    
    if (updates.name) {
      updateData.name = updates.name;
    }
    if (updates.avatar !== undefined) {
      updateData.avatar = sanitizeAvatarUrl(updates.avatar);
    }
    
    if (snapshot.exists()) {
      const existingData = snapshot.data();
      await updateDoc(userRef, updateData);
      
      // Update in all conversations
      await updateUserInConversations(
        userId, 
        updates.name || existingData.name,
        updates.avatar !== undefined ? updates.avatar : existingData.avatar
      );
      
      console.log(`Profile updated in Firestore for user ${userId}:`, updates);
    } else {
      // User doesn't exist - create them
      await setDoc(userRef, {
        name: updates.name || 'User',
        avatar: sanitizeAvatarUrl(updates.avatar),
        lastSeen: serverTimestamp(),
        online: true,
        typing: null,
        drafts: {},
        unreadCounts: {}
      });
      console.log(`Created new Firestore user ${userId}`);
    }
    
    return true;
  } catch (err) {
    console.error('Error updating user profile in Firestore:', err);
    throw err;
  }
}

/**
 * Update user's online status
 */
export async function setUserOnline(userId, online = true) {
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  
  await updateDoc(userRef, {
    online,
    lastSeen: serverTimestamp()
  });
}

/**
 * Set typing indicator for a conversation
 */
export async function setTyping(userId, conversationId) {
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  
  await updateDoc(userRef, {
    typing: {
      conversationId,
      timestamp: serverTimestamp()
    }
  });
}

/**
 * Clear typing indicator
 */
export async function clearTyping(userId) {
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  
  await updateDoc(userRef, {
    typing: null
  });
}

/**
 * Subscribe to a user's typing status
 */
export function subscribeToUserTyping(userId, callback) {
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  
  return onSnapshot(userRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.data();
      callback(data.typing);
    } else {
      callback(null);
    }
  }, (error) => {
    console.error('[Firestore] subscribeToUserTyping error:', error);
    callback(null);
  });
}

/**
 * Subscribe to typing indicators for a conversation
 * Returns list of user names currently typing in that conversation
 * 
 * Note: On mobile, Firestore onSnapshot can be unreliable. Consider using
 * WebSocket-based typing indicators for better mobile experience.
 */
export function subscribeToConversationTyping(conversationId, participantIds, currentUserId, callback) {
  if (!conversationId || !participantIds || participantIds.length === 0) {
    return () => {};
  }

  const unsubscribes = [];
  const typingUsers = new Map();
  
  const otherParticipants = participantIds.filter(id => id && id !== currentUserId);
  
  if (otherParticipants.length === 0) {
    return () => {};
  }
  
  otherParticipants.forEach(userId => {
    const userRef = doc(db(), COLLECTIONS.USERS, userId);
    
    const unsub = onSnapshot(userRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const typing = data.typing;
        
        if (typing && typing.conversationId === conversationId) {
          const typingTime = typing.timestamp?.toDate?.() || new Date(typing.timestamp);
          const now = new Date();
          
          if (now - typingTime < TYPING_TIMEOUT) {
            const userName = data.name || data.displayName || data.email?.split('@')[0] || 'Someone';
            typingUsers.set(userId, userName);
          } else {
            typingUsers.delete(userId);
          }
        } else {
          typingUsers.delete(userId);
        }
        
        callback(Array.from(typingUsers.values()));
      }
    }, (error) => {
      console.error('[Typing] Error subscribing to user:', userId, error);
    });
    
    unsubscribes.push(unsub);
  });
  
  return () => unsubscribes.forEach(unsub => unsub());
}

/**
 * Save a draft message for a conversation
 */
export async function saveDraft(userId, conversationId, text) {
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  
  if (text && text.trim()) {
    await updateDoc(userRef, {
      [`drafts.${conversationId}`]: text
    });
  } else {
    await updateDoc(userRef, {
      [`drafts.${conversationId}`]: deleteField()
    });
  }
}

/**
 * Get draft for a conversation
 */
export async function getDraft(userId, conversationId) {
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  const snapshot = await getDoc(userRef);
  
  if (snapshot.exists()) {
    const drafts = snapshot.data().drafts || {};
    return drafts[conversationId] || '';
  }
  
  return '';
}

/**
 * Clear draft after sending
 */
export async function clearDraft(userId, conversationId) {
  await saveDraft(userId, conversationId, '');
}

/**
 * Update unread count for a conversation
 */
export async function updateUnreadCount(userId, conversationId, count) {
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  
  if (count > 0) {
    await updateDoc(userRef, {
      [`unreadCounts.${conversationId}`]: count
    });
  } else {
    await updateDoc(userRef, {
      [`unreadCounts.${conversationId}`]: deleteField()
    });
  }
}

/**
 * Increment unread count
 */
export async function incrementUnreadCount(userId, conversationId) {
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  const snapshot = await getDoc(userRef);
  
  const currentCount = snapshot.exists() 
    ? (snapshot.data().unreadCounts?.[conversationId] || 0)
    : 0;
  
  await updateDoc(userRef, {
    [`unreadCounts.${conversationId}`]: currentCount + 1
  });
}

/**
 * Clear unread count (when user opens conversation)
 */
export async function clearUnreadCount(userId, conversationId) {
  await updateUnreadCount(userId, conversationId, 0);
}

/**
 * Get all unread counts for a user
 */
export async function getUnreadCounts(userId) {
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  const snapshot = await getDoc(userRef);
  
  if (snapshot.exists()) {
    return snapshot.data().unreadCounts || {};
  }
  
  return {};
}

/**
 * Subscribe to unread counts
 */
export function subscribeToUnreadCounts(userId, callback) {
  if (!isFirebaseSignedIn()) {
    console.warn('[Firestore] subscribeToUnreadCounts called before Firebase Auth ready, skipping');
    callback({});
    return () => {}; // Return no-op unsubscribe
  }
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  
  return onSnapshot(userRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.data().unreadCounts || {});
    } else {
      callback({});
    }
  }, (error) => {
    console.error('[Firestore] subscribeToUnreadCounts error:', error);
    callback({});
  });
}

/**
 * Get user profile
 */
export async function getUserProfile(userId) {
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  const snapshot = await getDoc(userRef);
  
  if (snapshot.exists()) {
    return { id: snapshot.id, ...snapshot.data() };
  }
  
  return null;
}

/**
 * Get all users from Firestore
 * Used for adding members to groups
 */
export async function getAllUsers() {
  const { collection, getDocs } = await import('firebase/firestore');
  const usersRef = collection(db(), COLLECTIONS.USERS);
  const snapshot = await getDocs(usersRef);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
}

/**
 * Subscribe to user's online status
 */
export function subscribeToUserOnline(userId, callback) {
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  
  return onSnapshot(userRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.data();
      callback({
        online: data.online || false,
        lastSeen: data.lastSeen
      });
    } else {
      callback({ online: false, lastSeen: null });
    }
  }, (error) => {
    console.error('[Firestore] subscribeToUserOnline error:', error);
    callback({ online: false, lastSeen: null });
  });
}

/**
 * Update user's avatar in Firestore and all their conversations
 * This is called after an avatar upload to ensure messaging system is in sync
 * 
 * @param {string} userId - The user's ID
 * @param {string} newAvatar - The new avatar URL
 * @param {string} userName - The user's display name (optional, to also update name)
 */
export async function updateUserAvatar(userId, newAvatar, userName = null) {
  try {
    const userRef = doc(db(), COLLECTIONS.USERS, userId);
    const snapshot = await getDoc(userRef);
    
    if (snapshot.exists()) {
      const existingData = snapshot.data();
      const updateData = {
        avatar: sanitizeAvatarUrl(newAvatar),
        lastSeen: serverTimestamp()
      };
      
      // Also update name if provided
      if (userName) {
        updateData.name = userName;
      }
      
      await updateDoc(userRef, updateData);
      
      // Update avatar in all conversations where this user participates
      await updateUserInConversations(
        userId, 
        userName || existingData.name, 
        newAvatar
      );
      
      console.log(`Avatar updated in Firestore for user ${userId}`);
    } else {
      // User doesn't exist in Firestore yet - create them
      await setDoc(userRef, {
        name: userName || 'User',
        avatar: sanitizeAvatarUrl(newAvatar),
        lastSeen: serverTimestamp(),
        online: true,
        typing: null,
        drafts: {},
        unreadCounts: {}
      });
      console.log(`Created new Firestore user ${userId} with avatar`);
    }
  } catch (err) {
    console.error('Error updating user avatar in Firestore:', err);
    throw err;
  }
}


// ==================== ARCHIVE CONVERSATIONS ====================

/**
 * Archive a conversation for a user (hides it from their list)
 * The conversation data remains intact - only hidden from this user's view
 *
 * Uses arrayUnion rather than read-modify-write: archive-for-everyone fans out
 * server-side with the Admin SDK's ArrayUnion, so writing back a whole array
 * read moments earlier could silently drop a concurrent entry.
 */
export async function archiveConversation(userId, conversationId) {
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  const snapshot = await getDoc(userRef);

  if (snapshot.exists()) {
    await updateDoc(userRef, {
      archivedConversations: arrayUnion(conversationId)
    });
  } else {
    // Create user doc if it doesn't exist
    await setDoc(userRef, {
      archivedConversations: [conversationId],
      lastSeen: serverTimestamp(),
      online: true
    }, { merge: true });
  }
}

/**
 * Archive a conversation for EVERY participant (chat creator / church admins only).
 *
 * The Firestore rules only allow a client to write its own users/{uid} doc, so
 * the fan-out has to go through the backend Admin SDK. Same semantics as the
 * per-user archive: a new message un-archives it again for everyone.
 *
 * Throws an Error whose message is the server's `detail` when the call fails.
 */
export async function archiveConversationForEveryone(userId, conversationId) {
  if (!API_URL) throw new Error("Couldn't archive for everyone");

  const res = await fetch(
    `${API_URL}/api/comms/v3/conversations/${encodeURIComponent(conversationId)}/archive-for-everyone`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId })
    }
  );

  if (!res.ok) {
    let detail = null;
    try {
      const body = await res.json();
      if (typeof body?.detail === 'string') detail = body.detail;
    } catch {
      // non-JSON error body — fall through to the generic message
    }
    throw new Error(detail || "Couldn't archive for everyone");
  }

  return res.json().catch(() => ({ success: true }));
}

/**
 * Unarchive a conversation for a user (shows it in their list again)
 * Called automatically when a new message arrives in an archived conversation
 */
export async function unarchiveConversation(userId, conversationId) {
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  const snapshot = await getDoc(userRef);
  
  if (snapshot.exists()) {
    // arrayRemove for the same reason as arrayUnion above: only this id moves.
    await updateDoc(userRef, {
      archivedConversations: arrayRemove(conversationId)
    });
  }
}

/**
 * Get list of archived conversation IDs for a user
 */
export async function getArchivedConversations(userId) {
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  const snapshot = await getDoc(userRef);
  
  if (snapshot.exists()) {
    return snapshot.data().archivedConversations || [];
  }
  
  return [];
}

/**
 * Subscribe to archived conversations changes
 */
export function subscribeToArchivedConversations(userId, callback) {
  if (!isFirebaseSignedIn()) {
    console.warn('[Firestore] subscribeToArchivedConversations called before Firebase Auth ready, skipping');
    callback([]);
    return () => {}; // Return no-op unsubscribe
  }
  const userRef = doc(db(), COLLECTIONS.USERS, userId);
  
  return onSnapshot(userRef, (snapshot) => {
    if (snapshot.exists()) {
      callback(snapshot.data().archivedConversations || []);
    } else {
      callback([]);
    }
  }, (error) => {
    console.error('[Firestore] subscribeToArchivedConversations error:', error);
    callback([]);
  });
}

